import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Spinner,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { DesktopIcon, ExclamationCircleIcon } from '@patternfly/react-icons';
import type RFB from '@novnc/novnc/lib/rfb';
import { useVmConsole } from '../../../hooks/useVmConsole';

interface VmConsoleTabProps {
  vmId: number;
}

const VmConsoleTab: React.FunctionComponent<VmConsoleTabProps> = ({ vmId }) => {
  const { consoleData, isLoading, error, requestConsole, reset } = useVmConsole(vmId);
  const rfbRef = useRef<RFB | null>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected'>('disconnected');
  const [rfbError, setRfbError] = useState<string | null>(null);

  useEffect(() => {
    if (!consoleData || !canvasContainerRef.current) return;

    let cancelled = false;

    if (rfbRef.current) {
      rfbRef.current.disconnect();
      rfbRef.current = null;
    }

    setConnectionStatus('connecting');
    setRfbError(null);

    import('@novnc/novnc/lib/rfb')
      .then(({ default: RFBClass }) => {
        if (cancelled || !canvasContainerRef.current) return;

        const rfb = new RFBClass(canvasContainerRef.current, consoleData.websocket_url);
        rfb.scaleViewport = true;
        rfb.resizeSession = false;

        rfb.addEventListener('connect', () => {
          setConnectionStatus('connected');
        });

        rfb.addEventListener('disconnect', (e) => {
          setConnectionStatus('disconnected');
          rfbRef.current = null;
          if (!e.detail.clean) {
            setRfbError('Console connection lost unexpectedly.');
          }
        });

        rfbRef.current = rfb;
      })
      .catch(() => {
        if (!cancelled) {
          setRfbError('Failed to load the VNC console module.');
          setConnectionStatus('disconnected');
        }
      });

    return () => {
      cancelled = true;
      if (rfbRef.current) {
        rfbRef.current.disconnect();
        rfbRef.current = null;
      }
    };
  }, [consoleData]);

  const handleSendCtrlAltDel = useCallback(() => {
    rfbRef.current?.sendCtrlAltDel();
  }, []);

  const handleFullscreen = useCallback(() => {
    canvasContainerRef.current?.requestFullscreen();
  }, []);

  const handleDisconnect = useCallback(() => {
    if (rfbRef.current) {
      rfbRef.current.disconnect();
      rfbRef.current = null;
    }
    setConnectionStatus('disconnected');
    setRfbError(null);
    reset();
  }, [reset]);

  // Loading state
  if (isLoading) {
    return (
      <EmptyState
        titleText="Requesting console..."
        icon={Spinner}
        variant="lg"
        headingLevel="h2"
        style={{ marginTop: '1rem' }}
      >
        <EmptyStateBody>Obtaining a console session for this virtual machine.</EmptyStateBody>
      </EmptyState>
    );
  }

  // API error state
  if (error) {
    return (
      <EmptyState
        titleText="Unable to open console"
        icon={ExclamationCircleIcon}
        variant="lg"
        headingLevel="h2"
        status="danger"
        style={{ marginTop: '1rem' }}
      >
        <EmptyStateBody>{error.message}</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" onClick={requestConsole}>
              Retry
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  // RFB disconnection error (after a successful connect)
  if (rfbError && !consoleData) {
    return (
      <EmptyState
        titleText="Console disconnected"
        icon={ExclamationCircleIcon}
        variant="lg"
        headingLevel="h2"
        status="danger"
        style={{ marginTop: '1rem' }}
      >
        <EmptyStateBody>{rfbError}</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" onClick={requestConsole}>
              Reconnect
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  // Connected / connecting state — show the canvas and toolbar
  if (consoleData) {
    return (
      <div style={{ marginTop: '1rem' }}>
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              <Button variant="secondary" onClick={handleSendCtrlAltDel} isDisabled={connectionStatus !== 'connected'}>
                Send Ctrl+Alt+Del
              </Button>
            </ToolbarItem>
            <ToolbarItem>
              <Button variant="secondary" onClick={handleFullscreen} isDisabled={connectionStatus !== 'connected'}>
                Fullscreen
              </Button>
            </ToolbarItem>
            <ToolbarItem align={{ default: 'alignEnd' }}>
              <Button variant="danger" onClick={handleDisconnect}>
                Disconnect
              </Button>
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>
        <div
          ref={canvasContainerRef}
          style={{
            width: '100%',
            height: '600px',
            background: '#000',
            marginTop: '0.5rem',
          }}
        />
        {connectionStatus === 'connecting' && (
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            <Spinner size="lg" /> Connecting to console...
          </div>
        )}
        {rfbError && (
          <div
            style={{
              textAlign: 'center',
              padding: '1rem',
              color: 'var(--pf-t--global--color--status--danger--default)',
            }}
          >
            {rfbError}
          </div>
        )}
      </div>
    );
  }

  // Initial state — prompt the user to connect
  return (
    <EmptyState
      titleText="Virtual machine console"
      icon={DesktopIcon}
      variant="lg"
      headingLevel="h2"
      style={{ marginTop: '1rem' }}
    >
      <EmptyStateBody>Connect to the virtual machine&apos;s VNC console.</EmptyStateBody>
      <EmptyStateFooter>
        <EmptyStateActions>
          <Button variant="primary" onClick={requestConsole}>
            Connect
          </Button>
        </EmptyStateActions>
      </EmptyStateFooter>
    </EmptyState>
  );
};

export default VmConsoleTab;
